import { Exceptions } from '@spectorasoftware/homegauge-shared';
import NavigationGuards from '../utilities/navigation-guards';
import RouteMeta from '../constants/route-meta';
import RouteNames from '../constants/route-names';

const validateRouteName = routeName => Object.values(RouteNames).includes(routeName);

/**
 * Callback used to intercept default Route passthrough.
 * MUST return `next()` once
 *
 * @description https://router.vuejs.org/guide/advanced/navigation-guards.html
 * @callback navigationGuard
 * @param {object} to - VueRouter route that user is attempting to access
 * @param {object} from - VueRouter route that user is coming from
 * @param {function} next - Callback to trigger VueRouter navigation
 */

class StepRoute {
  /**
   * @param {RouteNames} name
   * @param {string} path
   * @param {Component} component
   */
  constructor(name, path, component) {
    if (!name || !path || !component) {
      throw new Exceptions.ArgumentError('`name`, `path`, and `component` args are required');
    }

    if (!validateRouteName(name)) {
      throw new Exceptions.ArgumentError(`Route '${name}' is not a valid register-able Route`);
    }

    this.routeChildren = [];
    this.routeComponent = component;
    this.routeMeta = {};
    this.routeName = name;
    this.routeNavigationGuard = null;
    this.routePath = path;
    this.routeProps = true;
  }

  /**
   *
   * @param {StepRoute[]} childRoutes
   */
  hasChildRoutes(childRoutes) {
    this.routeChildren = childRoutes;
  }

  /**
   *
   */
  isAccountManagementStep() {
    this.routeMeta[RouteMeta.AccountManagementStep] = true;
  }

  /**
   * Navigation Guard function to run after
   * @param {navigationGuard} guardFunction
   */
  navigationGuard(guardFunction) {
    this.routeNavigationGuard = guardFunction;
  }

  /**
   * Set Requires Authentication` meta for navigation guard
   */
  requiresAuthentication() {
    this.routeMeta[RouteMeta.RequiresAuthentication] = true;
  }

  /**
   * Set `Property Step Required` meta for navigation guard
   */
  requiresPropertyStep() {
    this.routeMeta[RouteMeta.RequiresPropertyStep] = true;
  }

  /**
   * Set `Schedule Step Required` meta for navigation guard
   */
  requiresScheduleStep() {
    this.routeMeta[RouteMeta.RequiresScheduleStep] = true;
  }

  /**
   * Set `Summary Sidebar` meta for navigation guard
   */
  showSummarySidebar() {
    this.routeMeta[RouteMeta.ShowSummarySidebar] = true;
  }

  /**
   * Construct VueRouter Route object
   *
   * @returns {object}
   */
  getRoute() {
    return {
      beforeEnter: (to, from, next) => {
        const redirectRoute = NavigationGuards.getNavigationGuardRedirectRoute(to, from);
        if (redirectRoute) {
          next({ name: redirectRoute, params: to.params });
        } else if (this.routeNavigationGuard) {
          this.routeNavigationGuard(to, from, next);
        } else {
          next();
        }
      },
      children: this.routeChildren,
      component: this.routeComponent,
      meta: this.routeMeta,
      name: this.routeName,
      path: this.routePath,
      props: this.routeProps,
    };
  }
}

export { validateRouteName };
export default StepRoute;
